import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ApiRequestError, withQuery, type QueryParams } from '../../shared/api';
import { copyText } from '../../shared/clipboard';
import { dayEndIso, dayStartIso, isoToDayEnd, isoToDayStart } from '../../shared/filters';
import { useSession } from '../../shared/session';

/** Мелкие строительные блоки разделов админки (§13). */

/**
 * Секрет, который показывается один раз: PIN и ссылки приглашения (§7.1),
 * пароль сотрудника (§15.3).
 *
 * Значение всегда лежит в поле, а не в абзаце: во встроенном режиме портал
 * держит страницу в iframe без разрешения на буфер обмена, и копирование там
 * не работает вовсе. Отказ не проглатывается: значение выделяется само, а
 * подсказка называет сочетание клавиш — иначе человек закроет окно с надписью
 * «Скопировано» и без секрета, который повторно не выдаётся.
 */
export function SecretValue({
  title,
  value,
  ariaLabel,
  buttonLabel,
}: {
  title: string;
  value: string;
  ariaLabel: string;
  buttonLabel: string;
}) {
  const field = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<'idle' | 'copied' | 'manual'>('idle');

  const copy = async () => {
    const ok = await copyText(value);
    setState(ok ? 'copied' : 'manual');
    if (!ok) {
      field.current?.focus();
      field.current?.select();
    }
  };

  return (
    <div className="secret-value">
      <h3>{title}</h3>
      <input
        ref={field}
        className="secret"
        readOnly
        value={value}
        aria-label={ariaLabel}
        onFocus={(event) => event.currentTarget.select()}
      />
      <button type="button" onClick={() => void copy()}>
        {buttonLabel}
      </button>
      {state === 'copied' && (
        <p className="muted" role="status">
          Скопировано
        </p>
      )}
      {state === 'manual' && (
        <p className="error" role="alert">
          Браузер не дал скопировать — в портале это обычное дело. Значение выделено: нажмите{' '}
          {copyShortcut()}.
        </p>
      )}
    </div>
  );
}

/** На Mac сочетание другое, и «нажмите Ctrl+C» там сбивает с толку. */
function copyShortcut(): string {
  const platform = navigator.userAgent;
  return /Mac|iPhone|iPad/.test(platform) ? '⌘C' : 'Ctrl+C';
}

/**
 * Фильтр «с … по …» (§13, §14). Пользователь выбирает дни, API получает
 * границы: нижнюю включающую, верхнюю исключающую. Пересчёт живёт в одном
 * месте, иначе «по 15.08» в одном разделе значило бы не то же, что в другом.
 */
export function PeriodFilter({
  from,
  to,
  onFrom,
  onTo,
}: {
  from: string;
  to: string;
  onFrom: (iso: string) => void;
  onTo: (iso: string) => void;
}) {
  // Обе даты — один блок: при переносе панели фильтров «с» и «по» не должны
  // оказаться на разных строках, порознь они читаются как разные фильтры.
  return (
    <div className="toolbar__pair">
      <label>
        С
        <input
          type="date"
          value={isoToDayStart(from)}
          onChange={(event) => onFrom(dayStartIso(event.target.value))}
        />
      </label>
      <label>
        По
        <input
          type="date"
          value={isoToDayEnd(to)}
          onChange={(event) => onTo(dayEndIso(event.target.value))}
        />
      </label>
    </div>
  );
}

/**
 * §14, E9, сценарий 32. Выгрузка текущей отфильтрованной таблицы.
 *
 * Ссылка получает **тот же объект фильтров**, что и запрос списка, — отбор
 * в файле и на экране не может разойтись. Номера страницы в ней нет: выгружается
 * весь отбор, а не видимая страница.
 */
export function ExportCsvLink({ path, filters }: { path: string; filters: QueryParams }) {
  return (
    <a href={withQuery(path, filters)} download>
      <button type="button">Выгрузить CSV</button>
    </a>
  );
}

/** Ошибка запроса по-русски. Сетевые детали — в консоли браузера, не здесь. */
export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof ApiRequestError ? error.message : 'Не удалось выполнить запрос';
  return (
    <p className="error" role="alert">
      {message}
    </p>
  );
}

export function Pager({
  page,
  perPage,
  total,
  onPage,
}: {
  page: number;
  perPage: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / perPage));
  if (pages <= 1) return null;
  return (
    <nav className="pager" aria-label="Страницы">
      <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Назад
      </button>
      <span className="muted">
        Страница {page} из {pages} · всего {total}
      </span>
      <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Вперёд
      </button>
    </nav>
  );
}

/**
 * Модальный диалог на нативном <dialog>: Esc и клик по подложке закрывают,
 * фокус остаётся внутри — браузер делает это сам.
 *
 * `confirmClose` — вопрос, который задаётся перед закрытием любым способом.
 * Проверка обязана жить здесь, а не в вызывающем окне: действие по умолчанию
 * события `cancel` закрывает нативный `<dialog>` независимо от того, что
 * сделает переданный `onClose`, поэтому снаружи Esc не удержать.
 */
export function Dialog({
  title,
  onClose,
  confirmClose,
  children,
}: {
  title: string;
  onClose: () => void;
  confirmClose?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  // Вопрос задаётся внутри окна, а не системным confirm: системная плашка
  // поверх собственного диалога выглядит чужой и не показывает, что именно
  // потеряется. Пока вопрос висит, окно остаётся открытым.
  const [asking, setAsking] = useState(false);
  const requestClose = () => {
    if (confirmClose) {
      setAsking(true);
      return;
    }
    onClose();
  };
  // Обработчик события живёт дольше рендера, поэтому берёт свежую функцию
  // из ссылки: иначе Esc спрашивал бы по состоянию первого рендера.
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    const handleCancel = (event: Event) => {
      event.preventDefault();
      requestCloseRef.current();
    };
    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, []);

  return (
    <dialog
      ref={ref}
      className="dialog"
      onClick={(event) => {
        if (event.target === ref.current) requestCloseRef.current();
      }}
    >
      <div className="dialog__body">
        <header className="dialog__header">
          <h2>{title}</h2>
          {/* Не просто «Закрыть»: так же называется кнопка перевода записи
              в закрытый статус, и для скринридера это две одинаковые команды
              с разными последствиями. */}
          <button type="button" aria-label="Закрыть окно" onClick={() => requestCloseRef.current()}>
            ✕
          </button>
        </header>
        {asking && confirmClose && (
          <div className="dialog__ask" role="alertdialog" aria-label="Подтверждение закрытия">
            <span>{confirmClose}</span>
            <div className="dialog__ask-actions">
              <button type="button" onClick={() => setAsking(false)}>
                Остаться
              </button>
              {/* Не «Закрыть окно»: так уже называется крестик, и две кнопки
                  с одинаковой подписью неразличимы для скринридера. */}
              <button type="button" className="btn-danger" onClick={onClose}>
                Да, закрыть
              </button>
            </div>
          </div>
        )}
        {children}
      </div>
    </dialog>
  );
}

/**
 * Подтверждение необратимого действия — окном приложения, а не системным
 * `confirm`. Системное окно выглядит чужим, не показывает последствий и в
 * половине разделов спрашивало одной строкой то, что стоит объяснить.
 *
 * Вопрос задаётся через `useConfirm`, чтобы обработчик кнопки остался
 * однострочным: разводить в каждом разделе своё состояние «что подтверждаем» —
 * это тот же диалог, переписанный пять раз.
 */
export interface ConfirmRequest {
  title: string;
  question: string;
  /** Что именно случится: снимаемые права, обрываемые сессии, необратимость. */
  warning?: string;
  /** Глагол на кнопке — то же правило, что у кнопок переходов. */
  verb: string;
  danger?: boolean;
  onConfirm: () => void;
}

export function useConfirm(): { ask: (request: ConfirmRequest) => void; dialog: ReactNode } {
  const [pending, setPending] = useState<ConfirmRequest | null>(null);

  const dialog = pending ? (
    <Dialog title={pending.title} onClose={() => setPending(null)}>
      <p>{pending.question}</p>
      {pending.warning && <p className="muted">{pending.warning}</p>}
      <div className="form__actions">
        <button type="button" onClick={() => setPending(null)}>
          Отмена
        </button>
        <button
          type="button"
          className={pending.danger ? 'btn-danger' : 'btn-primary'}
          onClick={() => {
            pending.onConfirm();
            setPending(null);
          }}
        >
          {pending.verb}
        </button>
      </div>
    </Dialog>
  ) : null;

  return { ask: setPending, dialog };
}

/**
 * Плитка-переключатель отбора: показатель и фильтр под ним — одно и то же
 * место (§13, §14). Живёт в общем слое, потому что три раздела показывают
 * одинаковые сводки, и три копии этой кнопки разошлись бы в поведении —
 * `aria-pressed` в одной и не в другой заметил бы только скринридер.
 *
 * `alert` — для показателя, который сам по себе плохая новость (просрочка).
 */
export function StatToggle({
  value,
  label,
  active,
  alert,
  onClick,
}: {
  value: number | string;
  label: string;
  active: boolean;
  alert?: boolean;
  onClick: () => void;
}) {
  const className = ['stat', 'stat--link', 'stat--toggle'];
  if (alert) className.push('stat--alert');
  if (active) className.push('stat--active');

  return (
    <button type="button" className={className.join(' ')} aria-pressed={active} onClick={onClick}>
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
    </button>
  );
}

/** Строка «данных нет» на всю ширину таблицы. */
export function EmptyRow({ colSpan, loading }: { colSpan: number; loading: boolean }) {
  return (
    <tr>
      <td colSpan={colSpan} className="muted">
        {loading ? 'Загрузка…' : 'Записей нет'}
      </td>
    </tr>
  );
}

/** Значение «может отсутствовать». */
export function orDash(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

/**
 * §11.2. Номер задачи Bitrix24 — ссылка в портал, а не голый текст.
 *
 * Просмотр задачи — следующий шаг менеджера по сценарию §11.2; без ссылки номер
 * приходилось запоминать и искать в портале руками (QA 22.08.2026, дефект 6).
 * Адрес портала называет сервер в bootstrap — догадки по URL запрещены; без
 * него (автономный режим) номер остаётся текстом. Новая вкладка обязательна:
 * во встроенном режиме переход внутри iframe запер бы портал в самом себе.
 */
export function B24TaskLink({ taskId }: { taskId: number | null | undefined }) {
  const session = useSession();
  if (taskId === null || taskId === undefined) return <>—</>;
  if (!session.b24PortalUrl) return <>{taskId}</>;
  return (
    <a
      href={`${session.b24PortalUrl}/company/personal/user/0/tasks/task/view/${taskId}/`}
      target="_blank"
      rel="noreferrer"
    >
      {taskId}
    </a>
  );
}

/**
 * §12. Группа Bitrix24 объекта — ссылка в портал по тому же правилу, что и
 * задача: голый номер группы заставлял искать её в портале руками.
 */
export function B24GroupLink({ groupId }: { groupId: number | null | undefined }) {
  const session = useSession();
  if (groupId === null || groupId === undefined) return <>автономный</>;
  if (!session.b24PortalUrl) return <>группа {groupId}</>;
  return (
    <a href={`${session.b24PortalUrl}/workgroups/group/${groupId}/`} target="_blank" rel="noreferrer">
      группа {groupId}
    </a>
  );
}

/**
 * §13. Действия строки под одной кнопкой.
 *
 * Замер 18.08.2026: в «Расписаниях» три кнопки словами занимали 352 px —
 * больше любой колонки с данными, и половина таблицы уезжала за
 * горизонтальную прокрутку. Действие ищут осознанно, а данные читают глазами
 * по строке, поэтому под меню уходит то, что дешевле искать.
 *
 * Меню позиционируется относительно окна, а не строки: `.table-wrap` обрезает
 * содержимое по вертикали, и вложенный выпадающий список был бы срезан.
 *
 * Рендерится порталом в `document.body`: ячейка действий приклеена
 * (`td.row-actions { position: sticky }`), а липкая ячейка — собственный
 * контекст наложения, и меню внутри неё рисовалось бы ПОД ячейками следующих
 * строк, какой бы z-index у него ни стоял (QA 22.08.2026, прогон 3, дефект 2).
 */
export interface RowAction {
  label: string;
  onSelect: () => void;
  /** Необратимое или ограничивающее действие — красным, как отдельная кнопка. */
  danger?: boolean;
}

export function RowMenu({ actions, label = 'Действия' }: { actions: RowAction[]; label?: string }) {
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!anchor) return undefined;
    const close = () => setAnchor(null);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    // Клик по своей кнопке обрабатывает toggle, клик по пункту — сам пункт;
    // любой другой pointerdown закрывает. Так нажатие «⋯» соседней строки
    // сначала закрывает это меню, и открытым остаётся ровно одно.
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      close();
    };
    // Прокрутка сдвинула бы меню относительно своей строки — закрываем
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [anchor]);

  if (actions.length === 0) return null;

  const toggle = () => {
    if (anchor) {
      setAnchor(null);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setAnchor({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="row-menu__button"
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        aria-label={label}
        title={label}
        onClick={toggle}
      >
        ⋯
      </button>
      {anchor &&
        createPortal(
          <div
            ref={menuRef}
            className="row-menu"
            role="menu"
            style={{ top: anchor.top, right: anchor.right }}
          >
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                role="menuitem"
                className={
                  action.danger ? 'row-menu__item row-menu__item--danger' : 'row-menu__item'
                }
                onClick={() => {
                  setAnchor(null);
                  action.onSelect();
                }}
              >
                {action.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
